import { Platform } from '@angular/cdk/platform';
import { formatNumber } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, NgZone, OnDestroy, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AppLauncher } from '@capacitor/app-launcher';
import { Capacitor } from '@capacitor/core';
import { ActivityAction, IntentLauncher } from '@capgo/capacitor-intent-launcher';
import { distance, lineString, nearestPointOnLine, point, pointToLineDistance } from '@turf/turf';
import { ImageViewerComponent } from 'app/image-viewer/image-viewer.component';
import { saveAs } from 'file-saver';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { BackButtonService } from '../back-button.service';
import { CameraService } from '../camera.service';
import { CodaltComponent } from '../codalt.component';
import { CustomerProjectType } from '../customer-project-type.enum';
import { CustomerProject } from '../customer-project.interface';
import { CustomerType } from '../customer-type.enum';
import { LocalStorage } from '../local-storage.class';
import { PdfreportService } from '../pdfreport.service';
import { ConfirmDialogService } from '../services/confirm-dialog-service/confirm-dialog.service';
import { CustomerProjectService } from '../services/customer-project/customer-project.service';
import { Hectometre, HectometreGroup, HectometreService } from '../services/hectometre/hectometre.service';
import { PageTitleService } from '../services/page-title/page-title.service';
import { WorkareaService } from '../services/workarea/workarea.service';
import { Settings } from '../settings.class';
import { Status } from '../status.interface';
import { WorkareaImage } from '../workarea-image';
import { projectFitsWorkarea, Workarea } from '../workarea.class';
import { LocationSelectionDialogComponent } from './location-selection-dialog/location-selection-dialog.component';
import { FieldOptionsService } from './shared/field-options.service';
import { Address, LocationService } from './shared/location.service';
import { WorkareaHistoryDialogComponent } from './workarea-history-dialog/workarea-history-dialog.component';

type WorkareaImageView = WorkareaImage & {
    deleteButtonVisibleUntil: number;
    isOwnUser: boolean;
    isNewUpload: boolean;
};

@Component({
    selector: 'app-workarea',
    templateUrl: './workarea.component.html',
    styleUrls: ['./workarea.component.scss'],
    standalone: false,
})
export class WorkareaComponent extends CodaltComponent implements OnInit, OnDestroy {
    private static readonly DELETE_BUTTON_VISIBLE_FOR_MS = 60 * 60 * 1000;
    private readonly currentUserId = LocalStorage.getUser().id;

    workarea: Workarea;
    customerId: number;
    statuses: Array<Status> = [];
    loaded = false;
    newWorkarea = false;
    isWorkareaComponent = true;
    addresses: Array<string>;
    private nearbyPoles: Hectometre[] = [];
    public customerType: CustomerType;
    readonly CustomerType = CustomerType;
    public fieldOptions = {};
    fieldTranslations = {
        street: 'Straat',
        housenumber: 'Huisnummer',
        zipcode: 'Postcode',
        place: 'Plaats',
        type: 'Type',
        service: 'Service',
        servicedate: 'Datum',
        location_damage: 'Schade Locatie',
        status: 'Status',
        toplayer: 'Toplaag',
        wearlayer: 'Slijtlaag',
        execution: 'Uitvoering',
        roadblock: 'Afzetting',
        drillLocation: 'Boorlocatie',
        lane: 'Rijstrook',
        damage: 'Type',
        genpriority: 'Prioriteit',
        waymark: 'Wegmarkering',
        bottomlayer: 'Onderlaag',
        interlayer: 'Tussenlaag',
        status_id: 'Status',
        customer_project_id: 'Project',
        customer_id: 'Klantgebied',
        comments: 'Opmerkingen',
        road_number: 'Wegnummer',
        klinkerfloor: 'Betreft klinkervak',
        reinforcementgrid: 'Bewapeningsnet gebruiken',
        lng: 'Longitude',
        lat: 'Latitude',
    };

    public currentTimestamp = Date.now();
    public workareaImages: WorkareaImageView[] = [];
    projects: CustomerProject[];

    private isReserved = false;
    private forms = new Map<string, NgForm>([]);

    generatingReport = false;

    appleMapsAvailable = false;
    flitsmeisterAvailable = false;

    takingPhoto = false;
    private deleteButtonRefreshInterval: ReturnType<typeof setInterval> | null = null;

    hasHoles(): boolean {
        if (!this.workarea.geom_geojson || !this.workarea.geom_geojson.coordinates) {
            return false;
        }

        return !!this.workarea.geom_geojson.coordinates.length;
    }

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private dialog: MatDialog,
        private pdfreportService: PdfreportService,
        private workareaService: WorkareaService,
        public platform: Platform,
        private cdref: ChangeDetectorRef,
        private ngZone: NgZone,
        private customerProjectService: CustomerProjectService,
        private cameraService: CameraService,
        private locationService: LocationService,
        private hectometreService: HectometreService,
        private confirmDialog: ConfirmDialogService,
        private pageTitleService: PageTitleService,
        private backButtonService: BackButtonService,
        private fieldOptionService: FieldOptionsService
    ) {
        super();
    }

    @HostListener('window:beforeunload', ['$event'])
    unloadNotification($event: any) {
        if (this.isReserved && !this.takingPhoto) {
            this.workareaService.removeReservation(this.workarea);
        }
    }

    public openChangesBackActionCheck(): Promise<boolean> {
        return new Promise(resolve => {
            if (Array.from(this.forms.values()).filter(f => f.dirty).length > 0) {
                this.confirmDialog
                    .confirm(
                        'Niet opgeslagen wijzigingen',
                        `Wilt u de niet opgeslagen wijzigingen opslaan of verwerpen?`,
                        'Wijzigingen opslaan',
                        'Wijzigingen verwerpen'
                    )
                    .then(() => {
                        this.save();
                        resolve(false);
                    })
                    .catch(() => resolve(true));
            } else {
                resolve(true);
            }
        });
    }

    navigateBack(): void {
        this.backButtonService.navigateBack();
    }

    ngOnInit() {
        this.pageTitleService.setTitle('Werkmap');

        this.checkNaviApps();
        this.watchRouteParams(
            [this.RouteParamnames.customerId, this.RouteParamnames.workareaId],
            ({ customerId, workareaId }) => {
                this.customerId = +customerId;
                this.forms = new Map<string, NgForm>([]);
                this.workareaImages = [];
                this.syncDeleteButtonRefreshInterval();
                this.loaded = false;
                const id = +workareaId;
                const queryParams = this.route.snapshot.queryParams;

                if (isFinite(id) && id > 0) {
                    this.getWorkarea(id);
                } else {
                    // Get pending polygon once (it clears after reading) - only for new workareas
                    const pendingData = this.workareaService.getPendingPolygon();

                    this.workarea = new Workarea();
                    this.workarea.lat = pendingData?.centroid?.lat || Settings.mapCenter.lat || 0;
                    this.workarea.lng = pendingData?.centroid?.lng || Settings.mapCenter.lng || 0;
                    this.workarea.extra.servicedate = new Date(new Date().setDate(new Date().getDate() + 1));
                    this.workarea.extra.servicedate.setHours(8);
                    this.workarea.extra.servicedate.setMinutes(0);
                    this.workarea.extra.priority = 'Hoog - opgelost binnen een dag';
                    const projectId = queryParams['project'];

                    // Set the polygon data and area on the new workarea
                    if (pendingData) {
                        this.workarea.geom_geojson = pendingData.geojson;
                        // Set the calculated area directly
                        if (pendingData.area > 0) {
                            this.workarea.areas = [
                                {
                                    width: 1,
                                    length: Math.round(pendingData.area * 100) / 100,
                                },
                            ];
                        }
                    }

                    if (projectId) {
                        this.customerProjectService
                            .getProject(projectId)
                            .pipe(map(p => p.data))
                            .subscribe(project => {
                                this.customerType =
                                    project.type === CustomerProjectType.drillings
                                        ? CustomerType.drillings
                                        : CustomerType.general;

                                this.workarea.type = project.type;
                                this.workarea.customer_project_id = project.id;
                                this.newWorkarea = true;
                                this.reserveWorkarea();
                            });
                    } else {
                        this.customerType = LocalStorage.getCustomer()['customer_type'];
                        this.newWorkarea = true;
                        this.reserveWorkarea();
                    }
                }
            }
        );
    }

    history() {
        this.dialog.open(WorkareaHistoryDialogComponent, {
            panelClass: 'confirm-dialog',
            disableClose: false,
            data: {
                workarea: this.workarea,
                fieldTranslations: this.fieldTranslations,
                statuses: this.statuses,
            },
        });
    }

    pdf() {
        this.generatingReport = true;
        this.pdfreportService.getWorkareaPdf(this.workarea.id).subscribe(
            document => {
                this.generatingReport = false;
                saveAs(document, `werkvak_${this.workarea.number}.pdf`);
            },
            () => (this.generatingReport = false)
        );
    }

    getWorkarea(id: number) {
        // Preserve geom_geojson and areas from current workarea if they exist
        const existingGeojson = this.workarea?.geom_geojson;
        const existingAreas = this.workarea?.areas;

        this.workareaService
            .getDetails(id)
            .then(workarea => {
                if (workarea.deleted_at) {
                    this.isReserved = true;
                }
                this.workarea = workarea;
                this.pageTitleService.setTitle('Werkmap', this.getWorkareaTitleContext());
                this.workareaImages = this.workarea.images.map(image => this.createImageView(image));
                this.syncDeleteButtonRefreshInterval();

                // Restore geom_geojson if server didn't return it but we had it locally
                if (!this.workarea.geom_geojson && existingGeojson) {
                    this.workarea.geom_geojson = existingGeojson;
                }
                // Restore areas if server didn't return them but we had them locally
                if ((!this.workarea.areas || this.workarea.areas.length === 0) && existingAreas) {
                    this.workarea.areas = existingAreas;
                }

                this.customerType = this.workarea.type
                    ? this.workarea.type === CustomerProjectType.drillings
                        ? CustomerType.drillings
                        : CustomerType.general
                    : LocalStorage.getCustomer().customer_type;
                this.getOptionsAndStatusses();
                this.workarea.extra = this.workarea.extra ? this.workarea.extra : {};
                this.workarea.extra.servicedate = new Date(this.workarea.extra.servicedate || null);

                if (this.workarea.extra.length === 0) {
                    this.workarea.extra = {};
                }
                if (typeof this.workarea.areas === 'string') {
                    this.workarea.areas = JSON.parse(this.workarea.areas);
                    if (typeof this.workarea.areas === 'string') {
                        this.workarea.areas = [];
                    }
                }
                if (!this.workarea.extra.wearlayer) {
                    this.workarea.extra.wearlayer = 'Onbekend';
                }
                if (!this.workarea.extra.execution) {
                    this.workarea.extra.execution = 'Onbekend';
                }
                if (this.route.snapshot.queryParams['newLat']) {
                    this.workarea.lat = +this.route.snapshot.queryParams['newLat'];
                    this.workarea.lng = +this.route.snapshot.queryParams['newLng'];
                    this.subscriptions.add(
                        this.getLocations(this.workarea.lat, this.workarea.lng)
                            .pipe(catchError(() => of([])))
                            .subscribe()
                    );
                }
                setTimeout(() => {
                    this.loaded = true;
                    this.cdref.detectChanges();
                });
                this.customerProjectService.getProjects(false, this.workarea.customer_id).subscribe(projects => {
                    this.projects = projects.filter(p => projectFitsWorkarea(p, this.workarea));
                });
            })
            .catch(error =>
                this.confirmDialog
                    .confirm(error.message.error.error, error.message.error.errormessage, 'Terug')
                    .then(() => this.navigateBack())
            );
    }

    private getWorkareaTitleContext(): string {
        if (this.workarea?.number) {
            return [this.workarea.number, LocalStorage.getCustomer()?.customer_name].filter(Boolean).join(' - ');
        }

        const address = [this.workarea?.street, this.workarea?.housenumber].filter(Boolean).join(' ');
        if (address) {
            return address;
        }

        return this.workarea?.customerProject?.name || '';
    }

    private async checkNaviApps() {
        if (!Capacitor.isNativePlatform()) return;
        const isIOS = Capacitor.getPlatform() === 'ios';

        if (isIOS) {
            this.appleMapsAvailable = (await AppLauncher.canOpenUrl({ url: 'http://maps.apple.com/maps' })).value;
            this.flitsmeisterAvailable = false;
        } else {
            this.flitsmeisterAvailable =
                (await IntentLauncher.getApplicationIconAsync({ packageName: 'nl.flitsmeister' })
                    .then(r => r.icon !== '')
                    .catch(() => false)) ||
                (await IntentLauncher.getApplicationIconAsync({ packageName: 'nl.flitsmeister.flux' })
                    .then(r => r.icon !== '')
                    .catch(() => false));
        }
    }

    async openGoogleMaps() {
        const lat = this.workarea.lat;
        const lon = this.workarea.lng;
        const label = encodeURIComponent('DvdS nummer: ' + this.workarea.number);
        const isIOS = Capacitor.getPlatform() === 'ios';

        try {
            if (isIOS) {
                const { completed } = await AppLauncher.openUrl({ url: `comgooglemaps://?q=${lat},${lon}&zoom=16` });
                if (!completed) throw new Error('not completed');
            } else {
                await IntentLauncher.startActivityAsync({
                    action: ActivityAction.VIEW,
                    data: `geo:0,0?q=${lat},${lon}(${label})`,
                    packageName: 'com.google.android.apps.maps',
                });
            }
        } catch {
            window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`, '_blank');
        }
    }

    async openAppleMaps() {
        const lat = this.workarea.lat;
        const lon = this.workarea.lng;
        await AppLauncher.openUrl({ url: `maps://?q=${lat},${lon}` });
    }

    async openFlitsmeister() {
        const lat = this.workarea.lat;
        const lon = this.workarea.lng;
        const isIOS = Capacitor.getPlatform() === 'ios';

        if (isIOS) {
            await AppLauncher.openUrl({ url: `flitsmeister://?geo=${lat},${lon}` });
            return;
        }

        for (const packageName of ['nl.flitsmeister', 'nl.flitsmeister.flux']) {
            try {
                await IntentLauncher.startActivityAsync({
                    action: ActivityAction.VIEW,
                    data: `geo:${lat},${lon}?q=${lat},${lon}`,
                    packageName,
                });
                return;
            } catch {}
        }
    }

    save(forceWithoutImage = false) {
        let allValid = true;
        let errorMessage = '<b>Verbeter de volgende velden:</b><br>';
        this.forms.forEach((formGroup: NgForm) => {
            if (allValid && formGroup.status !== 'DISABLED') {
                allValid = formGroup.valid;
            }
            for (const name in formGroup.controls) {
                if (formGroup.controls[name].invalid) {
                    errorMessage += `${this.fieldTranslations[name] || name} is verplicht<br>`;
                }
                formGroup.controls[name].markAsTouched();
            }
        });

        if (!allValid) {
            this.confirmDialog.confirm('Controleer je invoer', errorMessage, 'Oke');

            return;
        }

        let stateHasImage = false;
        this.workareaImages.forEach(image => {
            if (image.isNewUpload) {
                image.status_id = this.workarea.status_id;
                image.user_id = this.currentUserId;
                image.workarea_id = this.workarea.id;
            }
            if (image.status_id == this.workarea.status_id) {
                stateHasImage = true;
            }
        });

        const noImageRequired = (workarea: Workarea) => {
            if (
                this.customerType === CustomerType.drillings ||
                (workarea.extra && workarea.extra['vogzang_type'] && workarea.extra['vogzang_type'] === 'Service')
            ) {
                return true;
            }
            return false;
        };

        this.statuses.forEach(status => {
            if (status.id == this.workarea.status_id) {
                this.workarea.status = status;
            }
        });

        if (
            !forceWithoutImage &&
            !noImageRequired(this.workarea) &&
            this.workarea.status?.imagerequired &&
            !stateHasImage
        ) {
            if (Settings.isApp()) {
                this.confirmDialog
                    .confirm('Een foto is verplicht', 'Wil je een foto maken?', 'Oké', 'Annuleren')
                    .then(() => this.takePhoto());
            } else {
                this.confirmDialog
                    .confirm(
                        'Een foto is verplicht voor deze status',
                        'Toch opslaan zonder foto?',
                        'Opslaan zonder foto',
                        'Status aanpassen'
                    )
                    .then(() => this.save(true));
            }
        } else {
            this.workarea.images = this.workareaImages.map(
                ({ deleteButtonVisibleUntil, isOwnUser, isNewUpload, ...image }) => image
            );
            this.workareaService
                .saveWorkarea(this.workarea)
                .then(data => {
                    this.forms.forEach(fg => fg.form.markAsPristine());
                    this.isReserved = false;
                    if (this.workarea.id) {
                        this.workareaService.getAll(true).then(() => this.navigateBack());
                    } else {
                        this.workarea = data['workarea'];
                        this.workareaService.addWorkareaToCacheList(this.workarea);
                        this.navigateBack();
                    }
                })
                .catch(error => {
                    if (error.error.error.error == 'WORKAREA_UPDATED_AT_OLDER_THEN_DBVERSION') {
                        let messageText = `${error.error.error.errormessage}\nU kunt een kopie opslaan met een nieuw nummer.`;
                        this.confirmDialog
                            .confirm('Opslaan is mislukt', messageText, 'Kopie Opslaan', 'Annuleren')
                            .then(() => {
                                this.workarea.comments = `[Conflict kopie van ${this.workarea.number}]\n${this.workarea.comments}`;
                                this.workarea.id = null;
                                this.workareaImages.forEach(img => (img.id = null));
                                this.save();
                            });
                    } else {
                        this.confirmDialog.confirm('Opslaan is mislukt', error.error.error.errormessage, 'Oké');
                    }
                });
        }
    }

    uploadFile(event) {
        const files = event.target.files;
        const maxSizeMB = 90;
        for (let i = 0; i < event.target.files.length; i++) {
            this.takingPhoto = true;
            if (files[i].size > maxSizeMB * 1024 * 1024) {
                this.confirmDialog
                    .confirm('Bestand te groot', `Dit bestand is groter als ${formatNumber(maxSizeMB, 'nl')} MB`, 'Oké')
                    .then(() => (this.takingPhoto = false));
            } else if (['image/jpeg', 'image/png'].indexOf(files[i].type) !== -1) {
                this.makeThumb(
                    base64 => {
                        this.uploadPhoto(base64 as string);
                    },
                    files[i],
                    1600
                );
            } else {
                this.confirmDialog
                    .confirm('Verkeerd bestand', 'Dit bestandsformaat wordt niet ondersteund', 'Oké')
                    .then(() => (this.takingPhoto = false));
            }
        }
    }

    takePhoto() {
        this.takingPhoto = true;
        this.cameraService
            .getPicture()
            .then((data: string) => this.uploadPhoto(data))
            .catch(() => (this.takingPhoto = false));
    }

    private uploadPhoto(data: string) {
        this.subscriptions.add(
            this.workareaService
                .saveWorkareaPicture(data, this.workarea.id, this.workarea.status_id, this.environment.apiEndpoint)
                .subscribe(
                    image =>
                        this.ngZone.run(() => {
                            image.data.workareaImage.status = {};
                            image.data.workareaImage.status['name'] = this.workarea.status_name;
                            image.data.workareaImage.user = LocalStorage.getUser();
                            const imageView = this.createImageView(image.data.workareaImage, true);
                            this.workareaImages.push(imageView);
                            this.workarea.images = [...this.workareaImages];
                            this.syncDeleteButtonRefreshInterval();
                            this.takingPhoto = false;
                        }),
                    () => {
                        this.confirmDialog.confirm(
                            'Foto maken mislukt',
                            'Er is iets misgegaan met het nemen van een foto.'
                        );
                        this.takingPhoto = false;
                    }
                )
        );
    }

    movingWorkarea() {
        this.router.navigate([this.Routenames.customers, this.workarea.customer_id], {
            queryParams: { movingWorkarea: this.workarea.id },
        });
    }

    setLocation(address: any) {
        let location: Partial<Address> & { road_number?: string };

        // Geolocation API results need to be normalized to extract the relevant address fields.
        if (address.address_components) {
            const components: Record<string, string> = {};
            address.address_components.forEach((comp: any) =>
                comp.types.forEach((t: string) => (components[t] = comp.long_name))
            );

            location = {
                street: components['route'],
                housenumber: components['street_number'],
                zipcode: components['postal_code'],
                town: components['locality'] || components['postal_town'],
            };
        } else if (address.street !== undefined) {
            // Nearby poles from the HectometreService already have a normalized structure, so we can use those directly.
            location = {
                street: address.street,
                town: address.place,
                road_number: address.road_number,
            };
        } else {
            // Fallback if only a formatted address is present.
            const parts = address.formatted_address.split(', ');
            location = {
                street: parts[0],
                town: parts[1],
            };
        }

        this.workarea.street = location.street || '-';
        this.workarea.housenumber = location.housenumber;
        this.workarea.zipcode = location.zipcode;
        this.workarea.place = location.town || '-';
        this.workarea.road_number = location.road_number;
    }

    getLocations(lat: string | number, lng: string | number) {
        return this.loadAddressOptions(
            of(this.nearbyPoles),
            this.locationService.getLocations(lat, lng, 'WorkareaComponent.getLocations')
        ).pipe(switchMap(addresses => this.showAddressSelection(addresses)));
    }

    public isFromValid(valid: NgForm, form) {
        this.forms.set(form, valid);
    }

    delete() {
        if (this.isReserved) {
            this.removeReservationAndNavigate();
        } else {
            this.confirmDialog
                .confirm(
                    'Verwijderen',
                    'Weet je zeker dat je deze boorlocatie wilt verwijderen?',
                    'Verwijderen',
                    'Behouden'
                )
                .then(() => this.removeReservationAndNavigate());
        }
    }

    ngOnDestroy(): void {
        if (this.isReserved) {
            this.workareaService.removeReservation({ ...this.workarea, id: this.route.snapshot.params['workareaId'] });
        }
        if (this.deleteButtonRefreshInterval) {
            clearInterval(this.deleteButtonRefreshInterval);
            this.deleteButtonRefreshInterval = null;
        }
        this.subscriptions.unsubscribe();
    }

    private reserveWorkarea() {
        const lat = Settings.mapCenter.lat;
        const lng = Settings.mapCenter.lng;

        // Both calls run in parallel. If both fail or return no data → show error dialog.
        // Otherwise show a selection dialog with whatever combination of poles and/or addresses is available.
        const nearby$ = this.hectometreService.nearby(lat, lng).pipe(
            map(result => this.buildNearbyPoles(result.data, lat, lng)),
            catchError(() => of(null))
        );

        const locations$ = this.locationService
            .getLocations(lat, lng, 'WorkareaComponent.reserveWorkarea')
            .pipe(catchError(() => of(null)));

        this.subscriptions.add(
            this.loadAddressOptions(nearby$, locations$)
                .pipe(switchMap(addresses => this.showAddressSelection(addresses)))
                .subscribe({
                    next: () => this.reserveCurrentWorkarea(),
                    error: () => {
                        this.confirmDialog
                            .confirm(
                                'Adres niet beschikbaar',
                                'De adressuggesties kunnen momenteel niet worden opgehaald. Probeer het opnieuw.',
                                'Opnieuw proberen',
                                'Annuleren'
                            )
                            .then(() => this.reserveWorkarea())
                            .catch(() => this.reserveCurrentWorkarea());
                    },
                })
        );
    }

    private loadAddressOptions(nearby$: Observable<Hectometre[] | null>, locations$: Observable<any[] | null>) {
        return forkJoin({ poles: nearby$, addresses: locations$ }).pipe(
            map(({ poles, addresses }) => {
                const safePoles = poles ?? [];
                const safeAddresses = addresses ?? [];
                const noNearby = safePoles.length === 0;
                const noLocations = addresses === null;

                if (noNearby && noLocations) {
                    throw new Error('no data');
                }

                this.nearbyPoles = safePoles;
                return [...safePoles, ...safeAddresses];
            })
        );
    }

    private reserveCurrentWorkarea() {
        this.getOptionsAndStatusses();
        this.workareaService.reserve(this.workarea).then(workarea => this.afterReserve(workarea));
    }

    private removeReservationAndNavigate() {
        this.workareaService.removeReservation(this.workarea).then(() =>
            this.router.navigate([this.Routenames.customers, this.workarea.customer_id], {
                queryParamsHandling: 'merge',
            })
        );
    }

    private showAddressSelection(addresses: any[]) {
        this.addresses = addresses;

        if (addresses.length > 1) {
            return this.dialog
                .open(LocationSelectionDialogComponent, {
                    width: '400px',
                    data: { addresses },
                })
                .afterClosed()
                .pipe(
                    tap(result => {
                        if (result) {
                            this.setLocation(result);
                        }
                    })
                );
        }

        if (addresses.length === 1) {
            this.setLocation(addresses[0]);
        }

        return of(null);
    }

    private buildNearbyPoles(groups: HectometreGroup[], lat: number, lng: number): Hectometre[] {
        const nearbyStrictDistanceMeters = 30;
        const nearbyFallbackOptionCount = 3;
        const calculatedPoint = point([lng, lat]);
        const poles: Hectometre[] = [];

        for (const group of groups) {
            for (const series of group.series) {
                if (series.poles.length < 2) {
                    continue;
                }

                const line = lineString(series.poles.map(pole => [pole.lng, pole.lat]));
                const distanceToLine = pointToLineDistance(calculatedPoint, line, { units: 'meters' });
                const snappedPoint = nearestPointOnLine(line, calculatedPoint, { units: 'meters' });

                const closestPole = series.poles.reduce((nearest, pole) =>
                    pole.distance < nearest.distance ? pole : nearest
                );

                const road_number = [
                    this.calculateRoadNumber(series.poles, snappedPoint, closestPole),
                    group.direction,
                    group.letter,
                ]
                    .filter(Boolean)
                    .join(' ');
                const label = `${group.waynumber} ${road_number}`;

                poles.push({
                    ...closestPole,
                    formatted_address: group.place ? `${label}, ${group.place}` : label,
                    distance: distanceToLine,
                    street: group.waynumber,
                    road_number,
                    place: group.place ?? '',
                });
            }
        }

        const sortedPoles = poles.sort((a, b) => a.distance - b.distance);
        const strictMatches = sortedPoles.filter(pole => pole.distance <= nearbyStrictDistanceMeters);

        if (strictMatches.length <= nearbyFallbackOptionCount) {
            return sortedPoles.slice(0, nearbyFallbackOptionCount);
        }

        return strictMatches;
    }

    private calculateRoadNumber(seriesPoles: Hectometre[], snappedPoint: any, closestPole: Hectometre): string {
        const interpolatableHectometreStep = 1;
        const segmentStartIndex = Math.min(snappedPoint.properties.index ?? 0, seriesPoles.length - 2);
        const segmentStartPole = seriesPoles[segmentStartIndex];
        const segmentEndPole = seriesPoles[segmentStartIndex + 1];
        const segmentStep = Math.abs(segmentEndPole.hectometre - segmentStartPole.hectometre);

        // Only interpolate when adjacent poles differ by exactly one hectometre step (0.1 km).
        // Larger jumps represent hectometre numbering discontinuities, not a continuous range
        // that can be safely interpolated, so in those cases we fall back to the nearest pole.
        if (segmentStep !== interpolatableHectometreStep) {
            return formatNumber(closestPole.hectometre / 10, 'nl', '1.3-3');
        }

        const segmentStartPoint = point([segmentStartPole.lng, segmentStartPole.lat]);
        const segmentEndPoint = point([segmentEndPole.lng, segmentEndPole.lat]);
        const segmentLength = distance(segmentStartPoint, segmentEndPoint, { units: 'meters' });
        const distanceFromSegmentStart = distance(segmentStartPoint, snappedPoint, { units: 'meters' });
        const segmentRatio = segmentLength > 0 ? Math.min(distanceFromSegmentStart / segmentLength, 1) : 0;
        const roadNumber =
            segmentStartPole.hectometre + (segmentEndPole.hectometre - segmentStartPole.hectometre) * segmentRatio;

        return formatNumber(roadNumber / 10, 'nl', '1.3-3');
    }

    private afterReserve(workarea: Workarea) {
        Object.assign(this.workarea, workarea);
        this.isReserved = true;
        if (this.customerType === CustomerType.general) {
            this.workarea.extra.execution = 'Dag';
            this.workarea.extra.wearlayer = 'Nee';
            this.workarea.extra.genpriority = 'Onbekend';
            this.workarea.extra.damage = 'Algemene schade';
        }
        this.getWorkarea(workarea.id);
        this.router.navigate([this.Routenames.customers, this.customerId, this.Routenames.workareas, workarea.id], {
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    private getOptionsAndStatusses() {
        this.workareaService.getAvailableStatuses(this.customerType).then(statuses => {
            this.statuses = statuses;
            if (this.newWorkarea) {
                this.statuses.forEach(status => {
                    if (status.isdefault) {
                        this.workarea.status = status;
                        this.workarea.status_id = status.id;
                    }
                });
            }
        });

        this.subscriptions.add(
            this.fieldOptionService
                .getOptions(this.customerType)
                .pipe(
                    map(options => {
                        let transformedOptions = { ...options };

                        if (options['waymark']) {
                            transformedOptions['waymark'] = [...options['waymark']];
                            if (
                                this.workarea.extra?.waymark &&
                                !transformedOptions['waymark'].includes(this.workarea.extra?.waymark)
                            ) {
                                transformedOptions['waymark'].push(this.workarea.extra?.waymark);
                            }
                        }

                        return transformedOptions;
                    })
                )
                .subscribe(options => (this.fieldOptions = options))
        );
    }

    private makeThumb(
        callback: (file: Blob | string) => void,
        inpFile: File,
        size: number,
        blob = false,
        convertToJpg = false
    ) {
        let resizedImage;
        const img = new Image();
        img.src = window?.URL.createObjectURL(inpFile);
        img.onload = () => {
            const factor = size / Math.max(img.naturalWidth, img.naturalHeight);
            const width = factor < 1 ? img.naturalWidth * factor : img.naturalWidth;
            const height = factor < 1 ? img.naturalHeight * factor : img.naturalHeight;
            const elem = document.createElement('canvas');
            elem.width = width;
            elem.height = height;
            const ctx = elem.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            if (blob) {
                ctx.canvas.toBlob(
                    blobFileC => {
                        callback(blobFileC);
                    },
                    'image/jpeg',
                    0.7
                );
            } else {
                if (inpFile.type === 'image/png' && !convertToJpg) {
                    resizedImage = ctx.canvas.toDataURL('image/png');
                } else {
                    resizedImage = ctx.canvas.toDataURL('image/jpeg', 0.7);
                }
                callback(resizedImage);
            }
        };
    }

    imageClick(image: WorkareaImageView) {
        const ref = this.dialog.open(ImageViewerComponent, {
            panelClass: 'image-viewer',
            width: '100vw',
            height: '100vh',
            maxWidth: '100vw',
            hasBackdrop: true,
            data: {
                images: this.workareaImages,
                currentImage: image,
                workarea: { ...this.workarea },
            },
        });
        const oldBack = this.backButtonService.getBackbuttonAction();
        this.backButtonService.setBackbuttonAction(() => {
            ref.close();
        });
        this.subscriptions.add(ref.afterClosed().subscribe(() => this.backButtonService.setBackbuttonAction(oldBack)));
    }

    public deleteImage(image: WorkareaImageView) {
        this.workareaService.deleteWorkareaPicture(image.id).subscribe(
            () => {
                this.workareaImages = this.workareaImages.filter(img => img.id !== image.id);
                this.currentTimestamp = Date.now();
                this.syncDeleteButtonRefreshInterval();
            },
            error => {
                this.currentTimestamp = Date.now();
                this.syncDeleteButtonRefreshInterval();
                this.confirmDialog.confirm('Fout bij verwijderen', error.message, 'Oke');
            }
        );
    }

    private createImageView(image: WorkareaImage, isNewUpload = false): WorkareaImageView {
        return {
            ...image,
            deleteButtonVisibleUntil: image?.created_at
                ? new Date(image.created_at).getTime() + WorkareaComponent.DELETE_BUTTON_VISIBLE_FOR_MS
                : 0,
            isOwnUser: image?.user_id === this.currentUserId,
            isNewUpload,
        };
    }

    /**
     * Checks if there are any delete buttons that should still be visible and sets or clears the interval that refreshes the current timestamp accordingly.
     * This ensures that delete buttons are shown and hidden at the correct times without needing to refresh the entire workarea data every minute.
     */
    private syncDeleteButtonRefreshInterval(): void {
        const hasVisibleDeleteButton = this.workareaImages.some(
            image => image.isOwnUser && image.deleteButtonVisibleUntil > this.currentTimestamp
        );

        if (hasVisibleDeleteButton && !this.deleteButtonRefreshInterval) {
            this.deleteButtonRefreshInterval = setInterval(() => {
                this.currentTimestamp = Date.now();
                this.cdref.detectChanges();
                this.syncDeleteButtonRefreshInterval();
            }, 60 * 1000);
        }

        if (!hasVisibleDeleteButton && this.deleteButtonRefreshInterval) {
            clearInterval(this.deleteButtonRefreshInterval);
            this.deleteButtonRefreshInterval = null;
        }
    }
}
